import React from 'react';
import DeleteForever from '@material-ui/icons/DeleteForever';
import queryString from 'query-string';
import Tooltip from '@material-ui/core/Tooltip';
import EditIcon from '@material-ui/icons/Edit';
import { RouteComponentProps } from 'react-router-dom';
import { showToast, Tables } from '@deeptrue-frontend/ui';
import { WorkbenchesReducerType } from 'store/modules/workbenches/workbenches.types';
import RouterProvider from 'components/RouterProvider/RouterProvider';
import { useFormatMessage } from 'components/IntlProvider';
import RemoveModal from 'components/PromptModal/PromptModal';
import api from 'api';

const { Link, ROUTES, withRouter } = RouterProvider;
const {
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHeader,
  TablePagination,
  TableFooter,
} = Tables;

interface WorkbenchesProps {
  currentPage: number;
  list: WorkbenchesReducerType['list']['results'];
  count: WorkbenchesReducerType['list']['count'];
  refresh: () => void;
}

const Workbenches = ({
  list,
  history,
  currentPage,
  count,
  refresh,
}: WorkbenchesProps & RouteComponentProps) => {
  const t = useFormatMessage();

  const onChangePage = (page: number) => {
    const params = { limit: 20, offset: 20 * page - 20 };
    history.push(
      `${ROUTES.ADMIN.WORKBENCHES.LIST}?${queryString.stringify(params)}`
    );
  };

  const onRemoveWorkbench = (id: number) => {
    api.resources
      .removeWorkbench({ id })
      .then(() => {
        refresh();
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      });
  };

  return (
    <Table>
      <TableHeader
        cells={[
          t('dashboard.workbenches.name', 'Name'),
          'ID',
          t('dashboard.workbenches.slug', 'Slug'),
          {
            text: t('dashboard.workbenches.resources', 'Resources'),
            props: { align: 'right' },
          },
        ]}
      />

      <TableBody>
        {list.map((workbench) => (
          <TableRow key={workbench.id}>
            <TableCell>{workbench.name}</TableCell>
            <TableCell>{workbench.id}</TableCell>
            <TableCell>{workbench.slug}</TableCell>
            <TableCell align="right">
              <Link to={`${ROUTES.ADMIN.WORKBENCHES.LIST}/${workbench.id}`}>
                <Tooltip
                  title={t('tooltip.workbenches.edit', 'Edit')}
                  placement="top"
                >
                  <EditIcon />
                </Tooltip>
              </Link>
              <span>
                <RemoveModal
                  onConfirm={() => onRemoveWorkbench(workbench.id)}
                  renderer={({ handleOpen }: { handleOpen: any }) => (
                    <Tooltip
                      title={t('tooltip.workbenches.remove', 'Remove')}
                      placement="top"
                    >
                      <DeleteForever onClick={handleOpen} />
                    </Tooltip>
                  )}
                >
                  <>
                    {t(
                      'pages.workbenches.modal.remove',
                      'Are you sure you want to remove { name } workbench',
                      {
                        name: workbench.name,
                      }
                    )}
                  </>
                </RemoveModal>
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        <TablePagination
          count={count}
          currentPage={currentPage}
          rowsPerPage={20}
          onChangePage={onChangePage}
        />
      </TableFooter>
    </Table>
  );
};

export default withRouter(Workbenches);
